import React, { useEffect, useRef } from 'react';

export interface IScriptProps {
  window: Window;
  document: Document;
  src?: string;
  children?: React.ReactNode;
  'data-embed-id'?: string;
  'data-selector'?: string;
}

export const Script = (props: IScriptProps) => {
  const { window, document } = props;
  const src = props.src;
  const text = typeof props.children === 'string' ? props.children : '';
  const embedId = props['data-embed-id'];
  const selector = props['data-selector'];
  const poll = useRef<number | null>(null);

  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    if (src) {
      script.src = src;
    } else if (text) {
      script.text = addPropertyToScriptText(text, { pathname: window.location.pathname });
    }

    // If this script is for an OpenGov embed tile
    if (embedId && selector && text?.indexOf('OpenGovSDK') > -1) {
      // Poll until the embed container div is in the DOM and the SDK's buildEmbed
      // function is in scope, then append the call script. This appends as soon as
      // both are ready (sub-second in standalone, where main.embed.js is cached),
      // while still tolerating PSP mode where the iframe loads main.embed.js
      // cross-origin from controlpanel without local CloudFront caching and can
      // take >2s to expose window.OpenGovSDK. A one-shot delay either painted late
      // (standalone) or, if too short, dropped the call and rendered blank (PSP);
      // the poll + generous cap avoids both.
      const POLL_INTERVAL_MS = 100;
      const MAX_WAIT_MS = 6000;
      let elapsed = 0;
      poll.current = window.setInterval(() => {
        // Also check for the 'targetNode': document.querySelector(selector)
        if (window.OpenGovSDK?.embed && document.querySelector(selector)) {
          document.body.appendChild(script);
          if (poll.current) {
            window.clearInterval(poll.current);
            poll.current = null;
          }
        } else if ((elapsed += POLL_INTERVAL_MS) >= MAX_WAIT_MS) {
          if (poll.current) {
            window.clearInterval(poll.current);
            poll.current = null;
          }
        }
      }, POLL_INTERVAL_MS);
    } else {
      document.body.appendChild(script);
    }

    return () => {
      if (poll.current) {
        window.clearInterval(poll.current);
        poll.current = null;
      }
    };
  }, [selector]);

  return <></>;
};

export function addPropertyToScriptText(scriptText: string, { pathname }: { pathname: string }) {
  const isPreview = pathname.includes('/preview');
  return scriptText.replace(
    /OpenGovSDK\.embed\(\{/g,
    `OpenGovSDK.embed({\n      canShowRemovedColumnsMessage: ${(!isPreview).toString()},`
  );
}
